import React from 'react';
import { 
  Grid, 
  Typography, 
  Box,
  Paper,
  Divider 
} from '@mui/material';
import { FaStar } from 'react-icons/fa';

// Sample concession menu items
const concessionItems = [
  {
    name: "Gourmet Popcorn",
    sizes: [
      { size: "Small", price: "$6.50" },
      { size: "Medium", price: "$7.50" },
      { size: "Large", price: "$8.50" }
    ],
    flavors: [
      "Traditional Butter",
      "White Cheddar",
      "Caramel",
      "Truffle Parmesan"
    ],
    image: "/images/concessions/popcorn.jpg",
    featured: true
  },
  {
    name: "Artisan Nachos",
    sizes: [
      { size: "Regular", price: "$8.95" },
      { size: "Large", price: "$11.95" }
    ],
    flavors: [
      "Classic Cheese",
      "Loaded (beef, jalapeños, sour cream)",
      "Guacamole & Salsa"
    ],
    image: "/images/concessions/nachos.jpg",
    featured: true
  },
  {
    name: "Premium Candy Selection",
    sizes: [
      { size: "Box", price: "$5.50" }
    ],
    flavors: [
      "Artisanal Chocolate Covered Almonds",
      "Gourmet Licorice",
      "Local Handcrafted Sweets",
      "Classic Movie Favorites"
    ],
    image: "/images/concessions/candy.jpg",
    featured: false
  },
  {
    name: "Beverages",
    sizes: [
      { size: "Small", price: "$5.50" },
      { size: "Medium", price: "$6.50" },
      { size: "Large", price: "$7.50" }
    ],
    flavors: [
      "Premium Sodas",
      "Sparkling Water",
      "Fresh Lemonade",
      "Specialty Iced Tea"
    ],
    image: "/images/concessions/beverages.jpg",
    featured: false
  },
  {
    name: "Gourmet Coffee Bar",
    sizes: [
      { size: "Regular", price: "$4.95" },
      { size: "Large", price: "$5.95" }
    ],
    flavors: [
      "Espresso",
      "Cappuccino",
      "Latte",
      "Mocha"
    ],
    image: "/images/concessions/coffee.jpg",
    featured: true
  },
  {
    name: "Snack Boxes",
    sizes: [
      { size: "Regular", price: "$9.95" }
    ],
    flavors: [
      "Movie Mix (nuts, dried fruits, chocolate)",
      "Cheese & Crackers",
      "Fresh Fruit & Yogurt Dip",
      "Mystery Box"
    ],
    image: "/images/concessions/snack-box.jpg",
    featured: false
  }
];

const ConcessionMenu = () => {
  return (
    <Box>
      <Grid container spacing={4}>
        {concessionItems.map((item, index) => (
          <Grid 
            item 
            xs={12} 
            md={6} 
            key={index}
            sx={{
              animation: `fadeIn ${0.3 + index * 0.1}s ease-out`,
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(20px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
            <Paper 
              elevation={3}
              sx={{ 
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 30px rgba(0,0,0,0.15)',
                }
              }}
            >
              <Box 
                sx={{ 
                  height: 180, 
                  background: `url(${item.image}) no-repeat center center`,
                  backgroundSize: 'cover',
                  position: 'relative'
                }}
              >
                {item.featured && (
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'secondary.main',
                      color: 'white',
                      py: 0.5,
                      px: 1.5,
                      borderRadius: 5,
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    }}
                  >
                    <FaStar style={{ marginRight: 4 }} /> FEATURED
                  </Box>
                )}
              </Box>
              
              <Box sx={{ p: 3, flexGrow: 1 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                  {item.name}
                </Typography>
                
                <Divider sx={{ my: 1.5 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Available Sizes:
                  </Typography>
                  <Grid container spacing={1}>
                    {item.sizes.map((sizeOption, sIdx) => (
                      <Grid item key={sIdx}>
                        <Box 
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 2,
                            py: 0.5,
                            px: 1.5
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {sizeOption.size}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, ml: 1, color: 'secondary.main' }}>
                            {sizeOption.price}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Options:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {item.flavors.map((flavor, fIdx) => (
                      <Box 
                        key={fIdx}
                        sx={{ 
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 2,
                          py: 0.5,
                          px: 1.5,
                          fontSize: '0.875rem'
                        }}
                      >
                        {flavor}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ConcessionMenu; 